import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format } from 'date-fns';

function ActivityLog() {
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    calls: 0,
    emails: 0,
    meetings: 0,
    linkedin_connects: 0,
    followups: 0,
    demos: 0,
    qualified_leads: 0,
    deals: 0,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'notes' || name === 'date' ? value : parseInt(value) || 0,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.post(`${API}/daily-activities`, formData);
      setMessage({ type: 'success', text: 'Activity logged successfully!' });
      // Reset form except date
      setFormData({
        ...formData,
        calls: 0,
        emails: 0,
        meetings: 0,
        linkedin_connects: 0,
        followups: 0,
        demos: 0,
        qualified_leads: 0,
        deals: 0,
        notes: '',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to log activity. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6" data-testid="activity-log-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Log Daily Activity</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Record your daily sales activities</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-8">
          {message.text && (
            <div
              className={`mb-6 px-4 py-3 rounded-lg ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
              role="alert"
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                max={format(new Date(), 'yyyy-MM-dd')}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                required
                data-testid="activity-date-input"
              />
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="calls" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Calls Made
              </label>
              <input
                type="number"
                id="calls"
                name="calls"
                value={formData.calls}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-calls-input"
              />
            </div>

            <div>
              <label htmlFor="emails" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Emails Sent
              </label>
              <input
                type="number"
                id="emails"
                name="emails"
                value={formData.emails}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-emails-input"
              />
            </div>

            <div>
              <label htmlFor="meetings" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Meetings Booked
              </label>
              <input
                type="number"
                id="meetings"
                name="meetings"
                value={formData.meetings}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-meetings-input"
              />
            </div>

            <div>
              <label htmlFor="linkedin_connects" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                LinkedIn Connects
              </label>
              <input
                type="number"
                id="linkedin_connects"
                name="linkedin_connects"
                value={formData.linkedin_connects}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-linkedin-input"
              />
            </div>

            <div>
              <label htmlFor="followups" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Follow-ups Completed
              </label>
              <input
                type="number"
                id="followups"
                name="followups"
                value={formData.followups}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-followups-input"
              />
            </div>

            <div>
              <label htmlFor="demos" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Demos Scheduled
              </label>
              <input
                type="number"
                id="demos"
                name="demos"
                value={formData.demos}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-demos-input"
              />
            </div>

            <div>
              <label htmlFor="qualified_leads" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Qualified Leads
              </label>
              <input
                type="number"
                id="qualified_leads"
                name="qualified_leads"
                value={formData.qualified_leads}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-qualified-leads-input"
              />
            </div>

            <div>
              <label htmlFor="deals" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Deals Closed
              </label>
              <input
                type="number"
                id="deals"
                name="deals"
                value={formData.deals}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="activity-deals-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              Notes (Optional)
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              placeholder="Add any additional notes or comments..."
              data-testid="activity-notes-input"
            ></textarea>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="activity-submit-button"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging Activity...
                </span>
              ) : (
                'Log Activity'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

export default ActivityLog;